import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

const INATIVIDADE_MINUTOS = 30;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Acesso resolvido pelo backend (ADR 0013 / #78): niveis {modulo: nivel} de /auth/me
  // e modulos liberados de /auth/me/modulos. As telas decidem abas e botoes por aqui,
  // nunca pelo cargo (user.perfil) — so 'administrador' ainda significa algo.
  const [acesso, setAcesso] = useState({ niveis: {}, modulos: null, pronto: false });
  const RANK = { leitor: 0, operador: 1, gestor: 2 };
  useEffect(() => {
    if (!user) { setAcesso({ niveis: {}, modulos: null, pronto: false }); return; }
    let vivo = true;
    Promise.all([authAPI.getMe().catch(() => ({ data: {} })), authAPI.getModulos().catch(() => ({ data: {} }))])
      .then(([me, mods]) => {
        if (!vivo) return;
        const lista = (mods.data?.modulos || []).filter(m => !m.bloqueado).map(m => m.codigo);
        setAcesso({ niveis: me.data?.niveis || {}, modulos: new Set(lista), pronto: true });
      });
    return () => { vivo = false; };
  }, [user]);
  const ehAdmin = user?.perfil === 'administrador';
  const nivel = (modulo) => (ehAdmin ? 'gestor' : (acesso.niveis[modulo] || null));
  const temNivel = (modulo, minimo) => { const n = nivel(modulo); return !!n && RANK[n] >= (RANK[minimo] ?? 0); };
  const podeModulo = (codigo) => ehAdmin || (acesso.modulos ? acesso.modulos.has(codigo) : false);
  const timeoutRef = React.useRef(null);

  // Detectar inatividade
  useEffect(() => {
    if (!user) return;

    const resetarTimeout = () => {
      // Limpar timeout anterior
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      // Criar novo timeout de inatividade
      timeoutRef.current = setTimeout(() => {
        logoutPorInatividade();
      }, INATIVIDADE_MINUTOS * 60 * 1000);
    };

    // Eventos que resetam o timer
    const eventos = ['mousemove', 'keydown', 'click', 'touchstart'];
    eventos.forEach(evento => {
      document.addEventListener(evento, resetarTimeout);
    });

    // Iniciar timeout na primeira vez
    resetarTimeout();

    // Cleanup
    return () => {
      eventos.forEach(evento => {
        document.removeEventListener(evento, resetarTimeout);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [user]);

  useEffect(() => {
    // Verificar se ja existe token salvo
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      const { access_token, usuario } = response.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(usuario));
      setUser(usuario);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Erro ao fazer login',
      };
    }
  };

  const loginWithGoogle = async (credential) => {
    try {
      const response = await authAPI.loginGoogle(credential);
      const { access_token, usuario } = response.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(usuario));
      setUser(usuario);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Erro ao fazer login com Google',
      };
    }
  };

  const logoutPorInatividade = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Erro ao fazer logout por inatividade:', error);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    alert(`Sua sessão foi encerrada por inatividade de ${INATIVIDADE_MINUTOS} minutos.`);
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const value = {
    user,
    loading,
    login,
    loginWithGoogle,
    logout,
    isAuthenticated: !!user,
    acesso,
    nivel,
    temNivel,
    podeModulo,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};
